import { chain, groupBy, head, map, pipe, prop, toPairs, values } from 'ramda';
import { noop } from 'ramda-adjunct';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { Text, TextButton } from '../../../../../components/common';
import { HorizontalSection } from '../../../../../components/common/HorizontalSection';
import { MainSource } from '../../../../../constants/Source';
import { encodePublicMediaFolderId } from '../../../../../utils/hoc/withPublicMedia/utils';
import { ICategoriesWithItems } from '../../../../../utils/hoc/withPublicMedia/withPublicMedia.types';
import { useTrackSearchAndFiltersAnalytics } from '../../../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics';
import { ClickOrigins } from '../../../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics/useTrackSearchAndFiltersAnalytics.types';
import { MediaManagerSDKAnyItemWithSectionInfo } from '../../../../../utils/media-manager-sdk';
import { RouteBuilder } from '../../../../../utils/RouteHelpers';
import { HomeSlider } from '../../../../Home/HomeSlider';
import styles from './OtherCategoriesResults.scss';
import { IOuter } from './OtherCategoriesResults.types';
import { NoResultsInCategory } from '../NoResultsInCategory/NoResultsInCategory';

export const OtherCategoriesResults: FC<IOuter> = ({
  items = [],
  searchQuery,
  onItemClick,
  selectedItems,
  isItemSelected,
  buyItem,
  onItemRightClick,
  isSecure,
  publicMediaCurrentCategory,
  publicMediaRootCategories,
  openExpandedView,
  currentFolder,
}) => {
  const subCategories = pipe(
    values,
    chain(prop('subFolders')),
    groupBy(prop('id')),
    map(head),
  )(publicMediaRootCategories);

  const categories = pipe<
    MediaManagerSDKAnyItemWithSectionInfo[],
    ICategoriesWithItems,
    [string, MediaManagerSDKAnyItemWithSectionInfo[]][]
  >(
    groupBy(prop('categoryId')),
    toPairs,
  )(items);

  const getCategoryId = (subCategoryId) => subCategories[subCategoryId].parentId;
  const getCategoryName = (subCategoryId) => publicMediaRootCategories[getCategoryId(subCategoryId)].name;
  const history = useHistory();

  const { trackSearchAndFiltersShowAllClick } = useTrackSearchAndFiltersAnalytics();

  const navigateToOtherCategorySearch = useCallback(
    (subCategoryId) => {
      const categoryId = getCategoryId(subCategoryId);

      history.push(
        RouteBuilder.Search(
          MainSource.MediaFromWix,
          searchQuery,
          encodePublicMediaFolderId({
            categoryId,
            subCategoryId,
          }),
        ),
      );
    },
    [history, getCategoryId, searchQuery],
  );

  const handleCategorySeeAllClick = useCallback((subCategoryId, event) => {
    event.stopPropagation();

    navigateToOtherCategorySearch(subCategoryId);

    trackSearchAndFiltersShowAllClick({
      target: subCategoryId,
      clickOrigin: ClickOrigins.Search,
    });
  }, []);

  const sections = map(
    ([categoryId, items]) => (
      <HorizontalSection
        key={categoryId}
        header={
          <div className={styles.sectionHeader}>
            <Text weight="bold" size="medium">
              {getCategoryName(categoryId)}
            </Text>

            {items[0].hasMoreItemsInThisCategory && (
              <TextButton
                size="small"
                onClick={(event) => handleCategorySeeAllClick(categoryId, event)}
                dataHook="see-all-button"
              >
                <FormattedMessage id="media-home.recent-files-section.see-all" />
              </TextButton>
            )}
          </div>
        }
      >
        <HomeSlider
          items={items}
          buyItem={buyItem}
          onItemClick={onItemClick}
          onItemRightClick={onItemRightClick}
          isItemSelected={isItemSelected}
          selectedItems={selectedItems}
          isSecure={isSecure}
          publicMediaCurrentCategory={publicMediaCurrentCategory}
          openExpandedView={openExpandedView}
          currentFolder={currentFolder}
          onNavLeftClick={noop}
          onNavRightClick={noop}
          beginDrag={noop}
          onItemDoubleClick={noop}
        />
      </HorizontalSection>
    ),
    categories,
  );

  return (
    <div className={styles.wrapper}>
      <NoResultsInCategory />

      <div className={styles.sections}>{sections}</div>
    </div>
  );
};
